import { flow, getSnapshot, types } from 'mobx-state-tree'
import { CustomerAccount, routes } from '@linqpal/models'
import { agingCode } from '@linqpal/models/src/dictionaries/agingCodes'
import { crudSideBarStore } from './crudSideBarStoreGenerator'
import { MessageSupport } from '../../types'
import { crudSideBar } from '../../../utils/types/enums'
import { parsePhoneNumber } from 'libphonenumber-js'
import { createContext, useContext } from 'react'
import { toDeleteRegexPhoneNumberMask } from '@linqpal/models/src/helpers/validations'
import { customerStatus } from '@linqpal/models/src/dictionaries/customerStatus'
import { toJS } from 'mobx'
import { TradeCreditStatus } from '@linqpal/models/src/dictionaries/TradeCreditStatus'

export const SupplierAccountsTabStore = types.compose(
  types
    .model('SupplierAccountsTabStore', {
      fetchParams: types.maybeNull(types.frozen()),
      items: types.array(types.frozen()),
      updatingCustomers: types.array(types.string),
    })
    .volatile((self) => ({
      mobileInputError: '',
      emailInputError: '',
      isExistingContractor: false,
      isCheckingExistingCustomer: false,
      isLoadingCreditInfo: false,
      isLoadingCustomerDetails: false,
      discardChangesConfirmationModal: false,
      viewAfterInvoice: false,
      onRefresh: undefined,
    }))
    .actions((self) => ({
      turnOnViewAfterInvoice() {
        self.viewAfterInvoice = true
      },
      turnOffViewAfterInvoice() {
        self.viewAfterInvoice = false
      },
      showDiscardChangesConfirmationModal() {
        self.discardChangesConfirmationModal = true
      },
      hideDiscardChangesConfirmationModal() {
        self.discardChangesConfirmationModal = false
      },
      resetMobileInputError() {
        self.mobileInputError = ''
      },
      resetEmailInputError() {
        self.emailInputError = ''
      },
      startLoadingCreditInfo: () => {
        self.isLoadingCreditInfo = true
      },
      stopLoadingCreditInfo: () => {
        self.isLoadingCreditInfo = false
      },
      startLoadingCustomerDetails: () => {
        self.isLoadingCustomerDetails = true
      },
      stopLoadingCustomerDetails: () => {
        self.isLoadingCustomerDetails = false
      },
      createCustomer: (type) => {
        self.showCreateSideBar()
        self.isExistingContractor = false
        self.draftModel.setValue('type', type)
      },
      fetchBillingContacts: flow(function* (customer) {
        try {
          self.startSending()
          const { billingContacts } = yield routes.supplier.billingContact(
            customer.id,
          )
          customer.setValue('contacts', billingContacts)
        } catch (e) {
          self.setErrorMessage(e.message)
        } finally {
          self.stopSending()
        }
      }),
      fetchCustomerDetails: flow(function* (customer) {
        try {
          self.startLoadingCustomerDetails()
          const customerDetails = yield routes.supplier.customerDetails(
            customer.id,
          )
          self.draftModel.setValue(
            'salesRepId',
            customerDetails.salesRepId || null,
          )
          self.draftModel.setValue(
            'salesRepUserInfo',
            customerDetails.salesRepUserInfo || null,
          )
          self.draftModel.setValue('bankAccounts', customerDetails.bankAccounts)
          self.draftModel.setValue('customer', customerDetails.customer)
          self.draftModel.setValue('customer_id', customer.id)
          self.draftModel.setValue('notes', customerDetails.notes)
        } catch (e) {
          self.setErrorMessage(e.message)
        } finally {
          self.stopLoadingCustomerDetails()
        }
      }),

      fetchCreditInfo: flow(function* (customer) {
        self.startLoadingCreditInfo()
        try {
          if (customer.customer?.id) {
            // self.startLoadingCreditInfo()

            const { creditInfo, agingList } = yield routes.supplier.creditInfo(
              customer.customer.id,
              customer.id,
            )

            const pastDueBreakdown = self.getPastDueBreakdown(agingList)
            const hasPastDue =
              [
                TradeCreditStatus.Approved,
                TradeCreditStatus.GoodStanding,
              ].includes(customer.credit_status) &&
              self.hasPastDue(pastDueBreakdown)

            self.draftModel.setValue('credit_info', {
              ...creditInfo,
              credit_status: hasPastDue
                ? TradeCreditStatus.PastDue
                : customer.credit_status,
              credit_status_valid_till: customer.credit_status_valid_till,
              past_due_breakdown: pastDueBreakdown,
            })
          } else {
            self.draftModel.setValue('credit_info', {
              statusInfo: { status: TradeCreditStatus.NotApplied },
            })
          }
        } catch (e) {
          self.setErrorMessage(e.message)
        } finally {
          self.stopLoadingCreditInfo()
        }
      }),
      getPastDueBreakdown(agingList) {
        const defaultBreakdown = {
          pastDue30: 0,
          pastDue60: 0,
          pastDue90: 0,
          pastDue120: 0,
          pastDue120Plus: 0,
        }
        if (!agingList) return defaultBreakdown

        const findAmount = (agingItem, code) =>
          agingItem?.agingDetails?.find((item) => item.code === code)?.amount ||
          0

        const pastDueBreakdown = agingList.reduce((total, current) => {
          return {
            pastDue30:
              total.pastDue30 + findAmount(current, agingCode.PastDue30),
            pastDue60:
              total.pastDue60 + findAmount(current, agingCode.PastDue60),
            pastDue90:
              total.pastDue90 + findAmount(current, agingCode.PastDue90),
            pastDue120:
              total.pastDue120 + findAmount(current, agingCode.PastDue120),
            pastDue120Plus:
              total.pastDue120Plus +
              (current.agingDetails
                ?.filter((item) =>
                  [
                    agingCode.PastDue150,
                    agingCode.PastDue180,
                    agingCode.PastDue180Plus,
                  ].includes(item.code),
                )
                ?.reduce((sum, item) => sum + item.amount, 0) || 0),
          }
        }, defaultBreakdown)

        return pastDueBreakdown
      },
      hasPastDue(pastDueBreakdown) {
        return (
          pastDueBreakdown?.pastDue30 ||
          pastDueBreakdown?.pastDue60 ||
          pastDueBreakdown?.pastDue90 ||
          pastDueBreakdown?.pastDue120 ||
          pastDueBreakdown?.pastDue120Plus
        )
      },
      setContactsError(customer, error) {
        customer.contacts.forEach((b) => {
          if (error.includes(b.email) || error.includes(b.phone))
            b.setValue(
              'error',
              'This Billing contact already exists. Please check details and try again',
            )
          else if (!error) b.setValue('error', error)
        })
      },
      setOnRefresh(value) {
        self.onRefresh = value
      },
      saveSalesRepId: flow(function* (selectedUser) {
        self.startSending()
        try {
          const updated = toJS(self.draftModel)
          updated.salesRepId = selectedUser._id
          delete updated.salesRepUserInfo
          yield routes.supplier.saveAccount(updated)
          self.draftModel.onSalesRepChange(selectedUser)
          self.closeAddSalesRep()
          self.refreshTable += 1
          self.onRefresh?.()
        } catch (e) {
          console.log(e)
          self.setErrorMessage('Changes could not be saved')
        } finally {
          self.stopSending()
        }
      }),
      submit: flow(function* (sendInvite = true, message) {
        self.startSending()
        try {
          yield routes.supplier.saveAccount(
            getSnapshot(self.draftModel),
            sendInvite,
          )
          self.setSuccessMessage(
            message || (self.isEdit ? 'Account Edited.' : 'Account Added.'),
          )
          self.createDefaultDraftModel()
          self.cancelAnySideBar()
          self.refreshTable += 1
        } catch (e) {
          self.setErrorMessage(e.message)

          if (self.draftModel.invited) {
            self.draftModel.setValue('invited', false)
          }
        } finally {
          self.stopSending()
        }
      }),
      submitContacts: flow(function* (customer) {
        self.startSending()
        try {
          yield routes.supplier.saveBillingContact(
            customer.id,
            customer.contacts,
          )
          self.setSuccessMessage('Billing contacts have been added')
          self.fetchBillingContacts(customer)
          self.refreshTable += 1
          self.closeAddContactSidebar(customer)
          self.setContactsError(customer, '')
          self.onRefresh?.()
        } catch (e) {
          self.setContactsError(customer, e.message)
        } finally {
          self.stopSending()
        }
      }),
      deleteAccount: flow(function* (id) {
        if (!id) {
          return
        }

        try {
          yield routes.supplier.deleteAccount(id)
          self.refreshTable += 1
        } catch (err) {
          self.setErrorMessage(err.message)
        }
      }),
      deleteContact: flow(function* (id, customer, index) {
        if (id) {
          self.startSending()
          try {
            yield routes.supplier.deleteContact(id)
            self.fetchBillingContacts(customer)
            self.refreshTable += 1
            self.closeAddContactSidebar(customer)
          } catch (e) {
            self.setErrorMessage(e.message)
          } finally {
            self.stopSending()
          }
        } else {
          customer.removeContact(index)
        }
      }),
      submitWithInvitation: flow(function* (account, isResend = false) {
        let invitationTo = self.draftModel.email
        if (account) {
          self.setDraftModel(account)
          invitationTo = account.email
        }
        self.draftModel.setValue('invited', true)
        yield self.submit(
          true,
          (account || isResend) &&
            `Invitation has been ${
              isResend ? 'resent' : 'sent'
            } to ${invitationTo}`,
        )
      }),
      showCreateWithInitialValue(value) {
        self.draftModel = CustomerAccount.create(value)
        self.setSideBar(crudSideBar.create)
      },
      checkIfCustomerExists: flow(function* () {
        try {
          if (self.draftModel.phone && self.draftModel.isValidPhone) {
            self.isCheckingExistingCustomer = true
            const phone = parsePhoneNumber(
              self.draftModel.phone,
              'US',
            ).nationalNumber
            const email = self.draftModel.email ?? undefined
            const id = self.draftModel.id ?? undefined
            const data = yield routes.supplier.checkExistingCustomer(
              phone,
              email,
              id,
            )
            if (data.error) {
              self.mobileInputError = data.error
              if (self.draftModel.email) {
                self.emailInputError = data.error
              }
              self.isExistingContractor = false
              self.isCachedContractor = false
            } else if (data.data && !self.isExistingContractor) {
              // isExistingContractor would be true if the user has already chosen the customer using the search - so avoid overwrite
              self.isExistingContractor = true
              self.isCachedContractor = true
              self.draftModel.setValue('first_name', data.data.firstName)
              self.draftModel.setValue('last_name', data.data.lastName)
              self.draftModel.setValue('name', data.data.businessName)
              self.draftModel.setValue('email', data.data.email)
              self.resetMobileInputError()
              self.resetEmailInputError()
            } else {
              self.resetMobileInputError()
              self.resetEmailInputError()
            }
          } else {
            self.isExistingContractor = false
            self.resetMobileInputError()
            self.resetEmailInputError()
            if (self.isCachedContractor) {
              self.draftModel.setValue('first_name', '')
              self.draftModel.setValue('last_name', '')
              self.draftModel.setValue('name', '')
              self.draftModel.setValue('email', '')
            }
          }
        } catch (e) {
          self.setErrorMessage(e.message)
        } finally {
          self.isCheckingExistingCustomer = false
        }
      }),
      // to be used with customer auto suggestion
      setSuggestedCustomerInfo: flow(function* (businessAccount) {
        self.draftModel.setValue('first_name', businessAccount.firstName)
        self.draftModel.setValue('last_name', businessAccount.lastName)
        self.draftModel.setValue('address', businessAccount.address)
        self.draftModel.setValue('name', businessAccount.name)
        self.draftModel.setValue('email', businessAccount.email)
        self.draftModel.setValue('contacts', businessAccount.contacts)
        self.draftModel.setValue(
          'phone',
          businessAccount.phone &&
            businessAccount.phone.replace(toDeleteRegexPhoneNumberMask, ''),
        )
      }),
      // Set Customer data with basic information for customer sidebar create modal
      setCustomerData: flow(function* (businessAccount) {
        const updateDraftModel = (key, value) => {
          if (typeof self.draftModel.setValue === 'function') {
            self.draftModel.setValue(key, value)
          } else {
            self.draftModel[key] = value
          }
        }

        try {
          Object.keys(businessAccount).forEach((key) => {
            const value =
              key === 'phone'
                ? businessAccount[key]
                  ? businessAccount[key].replace(
                      toDeleteRegexPhoneNumberMask,
                      '',
                    )
                  : ''
                : businessAccount[key]
            if (key === 'isBillingContactAdded') return
            updateDraftModel(key, value)
          })
        } catch (error) {
          self.setErrorMessage(error.message)
        }
      }),
      // using this function in customer screen to set customer data in store
      setPhoneAndContactFromBusiness(val) {
        if (!val) return
        self.resetMobileInputError()
        self.resetEmailInputError()
        self.isCheckingExistingCustomer = false
        self.isExistingContractor = true

        self.setSuggestedCustomerInfo(val)

        self.customerInputForceUpdate += 1
      },
      setData({ items }) {
        self.items.replace(items)
      },
    }))
    .views((self) => ({
      get getUpdatingCustomers() {
        console.log('HCHCHC', self.updatingCustomers)
        return self.updatingCustomers
      },
      get isBussinesFieldEditable() {
        const { status, name } = self.draftModel
        if (status === customerStatus.tradeCredit) return false // business name shouldn't be editable if the customer has an active credit
        return status !== customerStatus.active || !name
      },
      get canSaveInEditMode() {
        const phoneCheck = self.draftModel.phone
          ? self.draftModel.isValidPhone
          : false

        return (
          self.isEdit &&
          phoneCheck &&
          self.draftModel.isValidEmail &&
          self.draftModel.contact
        )
      },
    })),
  MessageSupport,
  crudSideBarStore(CustomerAccount),
)

export const TabAccountsContext = createContext(
  SupplierAccountsTabStore.create(),
)
export const TabAccountsProvider = TabAccountsContext.Provider

export const useTabAccount = () => useContext(TabAccountsContext)
